<template>
  <Header />
  <SideBar />
  <main id="main-content" class="ml-64 p-8">
    <!-- Page Header -->
    <div class="mb-8">
      <div class="flex items-center space-x-3 mb-2">
        <button @click="router.back()" class="text-gray-500 hover:text-gray-700">
          <i class="fa-solid fa-arrow-left"></i>
        </button>
        <h2 class="text-2xl font-bold text-green-800">{{ organization?.name || 'Organization' }}</h2>
        <span v-if="organization" :class="{
          'px-2 inline-flex text-xs leading-5 font-semibold rounded-full': true,
          'bg-green-100 text-green-800': organization.status === 'Active',
          'bg-yellow-100 text-yellow-800': organization.status === 'Inactive',
          'bg-red-100 text-red-800': organization.status === 'Disabled',
        }">{{ organization.status }}</span>
      </div>
      <p class="text-gray-600 text-sm ml-7">Organization profile and user management</p>
    </div>

    <!-- Profile Card -->
    <div v-if="organization" class="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
      <h3 class="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">Profile</h3>
      <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div>
          <p class="text-xs text-gray-500 uppercase tracking-wider mb-1">Organization Name</p>
          <div class="flex items-center space-x-2">
            <i class="fa-solid fa-building text-green-600"></i>
            <span class="text-sm font-medium text-gray-900">{{ organization.name }}</span>
          </div>
        </div>
        <div>
          <p class="text-xs text-gray-500 uppercase tracking-wider mb-1">Contact Person</p>
          <div class="flex items-center space-x-2">
            <i class="fa-solid fa-user text-green-600"></i>
            <span class="text-sm font-medium text-gray-900">{{ organization.contactName }}</span>
          </div>
        </div>
        <div>
          <p class="text-xs text-gray-500 uppercase tracking-wider mb-1">Contact Email</p>
          <div class="flex items-center space-x-2">
            <i class="fa-solid fa-envelope text-green-600"></i>
            <span class="text-sm font-medium text-gray-900">{{ organization.contactEmail }}</span>
          </div>
        </div>
        <div>
          <p class="text-xs text-gray-500 uppercase tracking-wider mb-1">Created</p>
          <div class="flex items-center space-x-2">
            <i class="fa-solid fa-calendar text-green-600"></i>
            <span class="text-sm text-gray-900">{{ formatDate(organization.createdAt) }}</span>
          </div>
        </div>
        <div>
          <p class="text-xs text-gray-500 uppercase tracking-wider mb-1">Last Updated</p>
          <div class="flex items-center space-x-2">
            <i class="fa-solid fa-clock text-green-600"></i>
            <span class="text-sm text-gray-900">{{ formatDate(organization.updatedAt) }}</span>
          </div>
        </div>
        <div>
          <p class="text-xs text-gray-500 uppercase tracking-wider mb-1">Account Balance</p>
          <div class="flex items-center space-x-2">
            <i class="fa-solid fa-wallet text-green-600"></i>
            <span v-if="billingStore.balanceLoading" class="text-sm text-gray-400">Loading...</span>
            <span v-else class="text-sm font-semibold text-gray-900">
              UGX {{ billingStore.balance !== null ? Number(billingStore.balance).toLocaleString('en-US', {
                minimumFractionDigits: 2, maximumFractionDigits: 2
              }) : '—' }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Subscriptions Section -->
    <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
      <div class="flex items-center justify-between mb-4">
        <h3 class="text-sm font-semibold text-gray-500 uppercase tracking-wider">Subscribed Services</h3>
        <span v-if="servicesStore.loading" class="text-xs text-gray-400">Loading...</span>
      </div>
      <div v-if="!servicesStore.loading && !servicesStore.subscriptions?.length"
        class="text-sm text-gray-400 text-center py-4">
        No services subscribed
      </div>
      <div v-else class="flex flex-wrap gap-2">
        <span v-for="sub in servicesStore.subscriptions" :key="sub.id" class="inline-flex items-center space-x-1.5 px-3 py-1.5 bg-green-50 border border-green-200
          text-green-800 text-xs font-medium rounded-full">
          <i class="fa-solid fa-cube text-green-600"></i>
          <span>{{ sub.serviceName }}</span>
          <span v-if="sub.rate" class="text-green-600 font-normal">· {{ sub.rate }}</span>
        </span>
      </div>
    </div>

    <!-- Users Section -->
    <div class="mb-6 flex items-center justify-between">
      <div>
        <h3 class="text-lg font-semibold text-gray-800">Users</h3>
        <p class="text-sm text-gray-500">Users registered under this organization</p>
      </div>
      <button @click="showRegisterModal = true" class="bg-green-800 hover:bg-green-700 text-white px-5 py-2.5 rounded-md flex items-center
        space-x-2 font-medium bg-gradient-to-r from-green-700 to-green-800 hover:from-green-700
        hover:to-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 rounded-lg
        focus:ring-green-500 transition-all duration-200 transform hover:scale-[1.02]">
        <i class="fa-solid fa-user-plus mr-1"></i>
        <span>Register User</span>
      </button>
    </div>

    <div class="bg-white rounded-lg shadow-sm border border-gray-200">
      <div v-if="userStore.loading" class="flex justify-center items-center h-48">
        <div class="animate-spin rounded-full h-10 w-10 border-b-2 border-green-600"></div>
      </div>
      <div v-else-if="!userStore.users?.length" class="p-8 text-center text-gray-500">
        No users found for this organization
      </div>
      <div v-else class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr v-for="user in userStore.users" :key="user.id">
              <td class="px-6 py-4 whitespace-nowrap">
                <div class="flex items-center">
                  <div class="h-9 w-9 rounded-full bg-green-800 flex items-center justify-center mr-3 flex-shrink-0">
                    <i class="fa-solid fa-user text-white text-sm"></i>
                  </div>
                  <div>
                    <div class="text-sm font-medium text-gray-900">{{ user.firstName }} {{ user.lastName }}</div>
                    <div class="text-sm text-gray-500">{{ user.emailAddress?.String }}</div>
                  </div>
                </div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <span class="px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded-full"
                  v-if="user.status === 'NOT_VERIFIED'">Not Verified</span>
                <span class="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full"
                  v-else-if="user.status === 'VERIFIED'">Verified</span>
                <span class="px-2 py-1 text-xs font-medium bg-indigo-100 text-indigo-800 rounded-full"
                  v-else-if="user.status === 'ACTIVE'">Active</span>
                <span class="px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full"
                  v-else-if="user.status === 'INACTIVE'">Inactive</span>
                <span v-else class="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-600 rounded-full">
                  {{ user.status }}
                </span>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {{ formatDate(user.createdAt) }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                <div class="flex items-center space-x-3">
                  <button @click="handleResetPassword(user.id)" title="Reset Password"
                    class="text-amber-600 hover:text-amber-800">
                    <i class="fa-solid fa-key"></i>
                  </button>
                  <button @click="openPermissionsModal(user)" title="Manage Permissions"
                    class="text-indigo-600 hover:text-indigo-800">
                    <i class="fa-solid fa-shield-halved"></i>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div v-if="userStore.pagination.totalPages && userStore.pagination.totalPages > 1"
        class="px-6 py-4 border-t border-gray-200">
        <div class="flex items-center justify-between">
          <div class="text-sm text-gray-700">
            Page <span class="font-medium">{{ userStore.pagination.currentPage }}</span> of
            <span class="font-medium">{{ userStore.pagination.totalPages }}</span>
          </div>
          <div class="flex items-center space-x-2">
            <button :disabled="currentPage <= 1" @click="changePage(currentPage - 1)"
              class="px-3 py-1 border border-gray-300 rounded text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50">
              Previous
            </button>
            <button :disabled="!userStore.pagination.nextPage" @click="changePage(currentPage + 1)"
              class="px-3 py-1 border border-gray-300 rounded text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50">
              Next
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Register User Modal -->
    <Modal :show="showRegisterModal" title="Register User" size="max-w-lg" @close="closeRegisterModal">
      <div v-if="registerLoading" class="flex justify-center items-center h-32">
        <div class="animate-spin rounded-full h-10 w-10 border-b-2 border-green-600"></div>
      </div>
      <form v-else class="space-y-5" @submit.prevent="handleRegisterUser">
        <div class="space-y-1">
          <label class="block text-sm font-medium text-gray-600">First Name</label>
          <input v-model="registerForm.firstName" type="text" required class="block w-full pl-4 pr-3 py-3 border border-gray-300 rounded-sm text-gray-600
            placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500
            focus:border-transparent" placeholder="John" />
        </div>
        <div class="space-y-1">
          <label class="block text-sm font-medium text-gray-600">Last Name</label>
          <input v-model="registerForm.lastName" type="text" required class="block w-full pl-4 pr-3 py-3 border border-gray-300 rounded-sm text-gray-600
            placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500
            focus:border-transparent" placeholder="Doe" />
        </div>
        <div class="space-y-1">
          <label class="block text-sm font-medium text-gray-600">Email Address</label>
          <input v-model="registerForm.emailAddress" type="email" required class="block w-full pl-4 pr-3 py-3 border border-gray-300 rounded-sm text-gray-600
            placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500
            focus:border-transparent" placeholder="john@example.com" />
          <p class="text-gray-500 text-xs">This will be the user's login username</p>
        </div>
        <div class="flex justify-end space-x-2 pt-2">
          <button type="button" @click="closeRegisterModal"
            class="px-4 py-2 bg-gray-200 text-gray-700 rounded-sm hover:bg-gray-300 text-sm">
            Cancel
          </button>
          <button type="submit" :disabled="userStore.loading" class="bg-green-800 hover:bg-green-700 text-white px-6 py-2 rounded-md flex items-center
            space-x-2 font-medium bg-gradient-to-r from-green-700 to-green-800 focus:outline-none
            focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-all duration-200
            disabled:opacity-50 text-sm">
            <i class="fa-solid fa-user-plus mr-2"></i>
            Register User
          </button>
        </div>
      </form>
    </Modal>

    <!-- Permissions Modal -->
    <Modal :show="showPermissionsModal" title="Manage Permissions" size="max-w-lg" @close="closePermissionsModal">
      <div v-if="permissionStore.loading && !permissionsUser" class="flex justify-center items-center h-32">
        <div class="animate-spin rounded-full h-10 w-10 border-b-2 border-green-600"></div>
      </div>
      <div v-else-if="permissionsUser">
        <!-- User identity strip -->
        <div class="flex items-center space-x-3 pb-4 mb-4 border-b border-gray-100">
          <div class="h-10 w-10 rounded-full bg-indigo-700 flex items-center justify-center flex-shrink-0">
            <i class="fa-solid fa-user text-white text-sm"></i>
          </div>
          <div>
            <p class="text-sm font-semibold text-gray-900">{{ permissionsUser.firstName }} {{ permissionsUser.lastName }}
            </p>
            <p class="text-xs text-gray-500">{{ permissionsUser.emailAddress?.String }}</p>
          </div>
        </div>

        <!-- Permissions list -->
        <div v-if="permissionStore.loading" class="flex justify-center items-center h-24">
          <div class="animate-spin rounded-full h-10 w-10 border-b-2 border-green-600"></div>
        </div>
        <div v-else-if="!permissionStore.permissions.length" class="py-6 text-center text-sm text-gray-400">
          No permissions available
        </div>
        <ul v-else class="space-y-2">
          <li v-for="perm in permissionStore.permissions" :key="perm.id"
            class="flex items-center justify-between px-3 py-2.5 rounded-md border"
            :class="isAssigned(perm.id) ? 'border-indigo-200 bg-indigo-50' : 'border-gray-200 bg-white'">
            <div class="flex items-center space-x-2 min-w-0">
              <i class="fa-solid fa-shield-halved text-sm flex-shrink-0"
                :class="isAssigned(perm.id) ? 'text-indigo-600' : 'text-gray-400'"></i>
              <span class="text-sm text-gray-800 font-mono truncate">{{ perm.permission }}</span>
              <span v-if="isAssigned(perm.id)"
                class="ml-1 px-1.5 py-0.5 text-xs font-medium bg-indigo-100 text-indigo-700 rounded flex-shrink-0">
                Assigned
              </span>
            </div>
            <button v-if="actioningPermissionId === perm.id" disabled
              class="ml-3 flex-shrink-0 w-7 h-7 flex items-center justify-center">
              <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600"></div>
            </button>
            <button v-else-if="isAssigned(perm.id)" @click="handleRemovePermission(perm.id)" title="Remove permission"
              class="ml-3 flex-shrink-0 px-2.5 py-1 text-xs font-medium text-red-600 border border-red-200
                rounded hover:bg-red-50 transition-colors">
              Remove
            </button>
            <button v-else @click="handleAssignPermission(perm.id)" title="Assign permission" class="ml-3 flex-shrink-0 px-2.5 py-1 text-xs font-medium text-indigo-600 border border-indigo-200
                rounded hover:bg-indigo-50 transition-colors">
              Assign
            </button>
          </li>
        </ul>

        <div class="flex justify-end pt-4 mt-2 border-t border-gray-100">
          <button @click="closePermissionsModal"
            class="px-4 py-2 bg-gray-100 text-gray-700 rounded-sm hover:bg-gray-200 text-sm">
            Done
          </button>
        </div>
      </div>
    </Modal>
  </main>
</template>

<script setup lang="ts">
import { onMounted, ref, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useOrganizationStore } from '@/store/organizations';
import { useUserStore } from '@/store/users';
import { useNotificationStore } from '@/store/notifications';
import { useBillingStore } from '@/store/billing';
import { usePermissionStore } from '@/store/permissions';
import { useServicesStore } from '@/store/services';
import type { Organization, User } from '@/config/types/responses';
import Header from '../components/header.vue';
import SideBar from '../components/sidebar.vue';
import Modal from '../components/modal.vue';

const route = useRoute();
const router = useRouter();
const organizationStore = useOrganizationStore();
const userStore = useUserStore();
const notificationStore = useNotificationStore();
const billingStore = useBillingStore();
const permissionStore = usePermissionStore();
const servicesStore = useServicesStore();

const organization = ref<Organization | null>(null);
const showRegisterModal = ref(false);
const registerLoading = ref(false);
const currentPage = ref(1);

const showPermissionsModal = ref(false);
const permissionsUser = ref<User | null>(null);
const actioningPermissionId = ref<string | null>(null);

const registerForm = ref({
  firstName: '',
  lastName: '',
  emailAddress: '',
});

const organizationId = computed(() => route.params.id as string);
onMounted(async () => {
  await organizationStore.fetchOrganizations(1);
  organization.value = organizationStore.organizationResults.find(
    (o) => o.id === organizationId.value,
  ) ?? null;

  userStore.fetchUsersByOrganization(organizationId.value, currentPage.value);
  billingStore.fetchOrganizationBalance(organizationId.value);
  servicesStore.fetchOrgSubscriptions(organizationId.value);
  permissionStore.fetchPermissions();
});

function formatDate(input: string) {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
  }).format(new Date(input));
}

function changePage(page: number) {
  currentPage.value = page;
  userStore.fetchUsersByOrganization(organizationId.value, page);
}

function closeRegisterModal() {
  showRegisterModal.value = false;
  registerForm.value = { firstName: '', lastName: '', emailAddress: '' };
  userStore.error = null;
}

async function openPermissionsModal(user: User) {
  permissionsUser.value = user;
  showPermissionsModal.value = true;
  await permissionStore.fetchUserPermissions(user.id);
}

function closePermissionsModal() {
  showPermissionsModal.value = false;
  permissionsUser.value = null;
  permissionStore.userPermissions = [];
  actioningPermissionId.value = null;
}

function isAssigned(permissionId: string): boolean {
  return permissionStore.userPermissions.some((p) => p.id === permissionId);
}

async function handleAssignPermission(permissionId: string) {
  if (!permissionsUser.value) return;
  actioningPermissionId.value = permissionId;
  await permissionStore.assignPermission(permissionsUser.value.id, permissionId);
  if (permissionStore.error) {
    notificationStore.showError('Assignment Failed', permissionStore.error);
  }
  actioningPermissionId.value = null;
}

async function handleRemovePermission(permissionId: string) {
  if (!permissionsUser.value) return;
  actioningPermissionId.value = permissionId;
  await permissionStore.removePermission(permissionsUser.value.id, permissionId);
  if (permissionStore.error) {
    notificationStore.showError('Removal Failed', permissionStore.error);
  }
  actioningPermissionId.value = null;
}

async function handleResetPassword(userId: string) {
  try {
    await userStore.resetPassword(userId);
    if (userStore.error) {
      notificationStore.showError('Reset Failed', userStore.error);
    } else {
      notificationStore.showSuccess('Password Reset', 'A new password has been sent to the user\'s email.');
    }
  } catch (err) {
    notificationStore.showError('Reset Error', 'An unexpected error occurred.');
  }
}

async function handleRegisterUser() {
  if (!registerForm.value.firstName || !registerForm.value.lastName || !registerForm.value.emailAddress) {
    notificationStore.showError('Validation Error', 'Please fill in all required fields.');
    return;
  }

  registerLoading.value = true;
  try {
    await userStore.registerUser(
      registerForm.value.firstName,
      registerForm.value.lastName,
      registerForm.value.emailAddress,
      organizationId.value,
    );

    if (userStore.error) {
      notificationStore.showError('Registration Failed', userStore.error);
    } else {
      notificationStore.showSuccess('User Registered', 'The user has been registered successfully.');
      closeRegisterModal();
      userStore.fetchUsersByOrganization(organizationId.value, currentPage.value);
    }
  } catch (err) {
    notificationStore.showError('Registration Error', 'An unexpected error occurred.');
  } finally {
    registerLoading.value = false;
  }
}
</script>
